import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

const getWeekRange = (isoStr) => {
    const match = isoStr.match(/^(\d{4})-W(\d{2})$/);
    if (!match) return isoStr;
    const year = parseInt(match[1], 10);
    const week = parseInt(match[2], 10);
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7;
    const mon = new Date(jan4);
    mon.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    const fmt = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
    return `${fmt(mon)}-${fmt(sun)}`;
};

const formatLabel = (raw, granularity) => {
    if (granularity === 'week') return getWeekRange(raw);
    if (granularity === 'month') {
        const parts = raw.split('-');
        return `${parseInt(parts[1], 10)}月`;
    }
    const d = new Date(raw);
    return `${d.getMonth() + 1}/${d.getDate()}`;
};

const TaxiTimeSeriesChart = ({
    label,
    data,
    height = 140,
    showLabels = true,
}) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        if (!chartRef.current || !data) return;

        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }

        const g = data.granularity || 'day';
        const dates = data.dates.map((d) => formatLabel(d, g));

        const option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params) => {
                    const idx = params[0].dataIndex;
                    const dateLabel = dates[idx];
                    const legCount = data.leg_counts?.[idx];
                    const legInfo = legCount != null ? `<br/>有效航段数: ${legCount}` : '';
                    const outVal = params[0].value;
                    const inVal = params[1].value;
                    const outText = outVal != null ? `${outVal} min` : '无出发航班';
                    const inText = inVal != null ? `${inVal} min` : '无到达航班';
                    return `<strong>${label}</strong> - ${dateLabel}<br/>
                            ${params[0].marker}Taxi Out: ${outText}<br/>
                            ${params[1].marker}Taxi In: ${inText}${legInfo}`;
                },
                textStyle: { fontSize: 11 },
            },
            grid: {
                left: '3%',
                right: '3%',
                bottom: '15%',
                top: showLabels ? '20%' : '10%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                data: dates,
                axisLabel: {
                    rotate: g === 'day' ? 45 : 0,
                    fontSize: 9,
                    color: '#8c8c8c',
                    interval: dates.length <= 40 ? 0 : Math.floor(dates.length / 10),
                },
                axisLine: { lineStyle: { color: '#d9d9d9' } },
                axisTick: { show: false },
            },
            yAxis: {
                type: 'value',
                name: 'min',
                nameTextStyle: { fontSize: 9, color: '#8c8c8c' },
                axisLabel: { fontSize: 9, color: '#8c8c8c', formatter: '{value}' },
                splitLine: { lineStyle: { color: '#f0f0f0' } },
                axisLine: { show: false },
            },
            series: [
                {
                    name: 'Taxi Out',
                    type: 'bar',
                    data: data.taxi_out_values,
                    itemStyle: { color: '#fa8c16' },
                    label: {
                        show: showLabels,
                        position: 'top',
                        formatter: (p) => (p.value != null && p.value > 0 ? Number(p.value).toFixed(0) : ''),
                        fontSize: 10,
                        color: '#fa8c16',
                        fontWeight: 500,
                    },
                    barGap: '15%',
                    barWidth: '30%',
                },
                {
                    name: 'Taxi In',
                    type: 'bar',
                    data: data.taxi_in_values,
                    itemStyle: { color: '#52c41a' },
                    label: {
                        show: showLabels,
                        position: 'top',
                        formatter: (p) => (p.value != null && p.value > 0 ? Number(p.value).toFixed(0) : ''),
                        fontSize: 10,
                        color: '#52c41a',
                        fontWeight: 500,
                    },
                    barWidth: '30%',
                },
            ],
        };

        chartInstance.current.setOption(option);

        const handleResize = () => chartInstance.current?.resize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [label, data, showLabels]);

    useEffect(() => {
        return () => {
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, []);

    if (!data || data.dates.length === 0) {
        return (
            <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c', fontSize: '12px' }}>
                暂无数据
            </div>
        );
    }

    return (
        <div
            ref={chartRef}
            style={{ width: '100%', height: `${height}px`, background: 'white', borderRadius: '4px' }}
        />
    );
};

export const TaxiChartRow = ({ label, data, loading = false }) => {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px',
                padding: '8px',
                background: '#fafafa',
                borderRadius: '4px',
                border: '1px solid #f0f0f0',
                transition: 'all 0.3s',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f5f5f5';
                e.currentTarget.style.borderColor = '#d9d9d9';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fafafa';
                e.currentTarget.style.borderColor = '#f0f0f0';
            }}
        >
            <div style={{
                minWidth: '100px',
                maxWidth: '100px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#1890ff',
                textAlign: 'center',
                padding: '8px 12px',
                background: 'white',
                borderRadius: '4px',
                border: '1px solid #e6f7ff',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}>
                {label}
            </div>

            <div style={{ flex: 1 }}>
                {loading ? (
                    <div style={{ height: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c', fontSize: '12px' }}>
                        加载中...
                    </div>
                ) : (
                    <TaxiTimeSeriesChart
                        label={label}
                        data={data}
                        height={140}
                        showLabels={data?.dates?.length <= 30}
                    />
                )}
            </div>
        </div>
    );
};

export default TaxiTimeSeriesChart;
